import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { formatMoney, syncWalletMirrors, walletBalanceCents } from '../utils/earnings';
import CorrectionsAPI from '../api/CorrectionsAPI';
import { EarningRow } from '../types/CorrectionModel';
import AppContext from '../context/AppContext';

// A second face for the header wallet, for full-screen overlays (the repair session, the game overlay)
// that hide the header while money is being earned. earnings.js owns the balance, keeps every mounted
// mirror in sync and aims the payout chip at the visible one. This reads the opening value at mount
// rather than starting at $0.00. Tapping it opens the last five movements, like the header counter.
export default function WalletMirror(): React.ReactNode {
  const [openingCents] = useState(() => walletBalanceCents());
  const { isAuthenticated, openLogin } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<EarningRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  // The balance can move before the node exists, and earnings.js may load late; repaint on mount.
  useEffect(() => {
    syncWalletMirrors();
  }, []);

  // Fetched per open: it's opened right after money moves, when a cached list would be a row short.
  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const data = await CorrectionsAPI.getRecentEarnings(5);
      setRows(data?.Items ?? []);
    } catch {
      setRows([]); // the popover still explains itself without the list
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  // Close on an outside click or Escape; the popover lives inside a modal.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span className="sc-wallet-mirror-wrap" ref={wrapRef}>
      <button
        type="button"
        className="sc-wallet-mirror"
        data-sc-wallet-mirror
        aria-expanded={open}
        title="Your repair earnings"
        onClick={(e) => {
          // Stop the document listener above from closing it again.
          e.stopPropagation();
          toggle();
        }}
      >
        <span className="sc-wallet-coin" aria-hidden="true">
          $
        </span>
        <span className="sc-wallet-mirror-amount" data-sc-wallet-mirror-amount>
          {(openingCents / 100).toFixed(2)}
        </span>
        <span className="sc-wallet-fx" data-sc-wallet-fx aria-hidden="true" />
      </button>
      {open ? (
        <div className="sc-wallet-mirror-pop" role="dialog" aria-label="Recent repair earnings">
          {!isAuthenticated ? (
            <>
              <p className="sc-wallet-pop-title">You're earning in demo mode</p>
              <p className="sc-wallet-pop-note sc-wallet-pop-warn mb-2">
                This balance disappears when you close the tab.
              </p>
              <button type="button" className="btn btn-primary btn-sm w-100" onClick={openLogin}>
                Create an account
              </button>
            </>
          ) : loading && rows == null ? (
            <p className="sc-wallet-pop-note mb-0">Loading…</p>
          ) : rows != null && rows.length > 0 ? (
            <>
              <p className="sc-wallet-recent-title">Recently</p>
              <ul className="sc-wallet-recent-list">
                {rows.map((row) => (
                  <li key={row.Id}>
                    <a
                      className="sc-wallet-recent-link"
                      href={`/#/?gameId=${row.GameId}${row.MoveRef ? `&move=${row.MoveRef}` : ''}`}
                    >
                      <span
                        className={`sc-wallet-recent-row ${row.Success ? 'is-win' : 'is-loss'}`}
                      >
                        <span className="sc-wallet-recent-amount">
                          {row.AmountCents >= 0 ? '+' : '−'}
                          {formatMoney(row.AmountCents)}
                        </span>
                        <span className="sc-wallet-recent-what">{describe(row)}</span>
                        <span className="sc-wallet-recent-when">{timeAgo(row.CreatedAt)}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
              <a className="sc-wallet-recent-all" href="/Identity/Account/Manage/Earnings">
                See every transaction →
              </a>
            </>
          ) : (
            <p className="sc-wallet-pop-note mb-0">
              Nothing yet. Repair a mistake and it pays what the mistake cost you.
            </p>
          )}
        </div>
      ) : null}
    </span>
  );
}

// "41. Nc3 · blunder · vs Boscobilly". Either half can be missing on an old row.
function describe(row: EarningRow): string {
  const bits: string[] = [];
  if (row.MoveNumber != null && row.San) bits.push(`${row.MoveNumber}. ${row.San}`);
  else if (row.San) bits.push(row.San);
  if (row.MoveType) bits.push(row.MoveType.toLowerCase());
  if (row.Opponent) bits.push(`vs ${row.Opponent}`);
  return bits.join(' · ');
}

function timeAgo(iso: string): string {
  const then = Date.parse(iso);
  if (isNaN(then)) return '';
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 90) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
